import React, { useCallback, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faFile,
  faFileCsv,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";

import OverlayLoading from "../shared/components/OverlayLoading";
import Title from "../shared/components/Title";
import DropzoneComponent from "../shared/components/DropzoneComponent";
import WorkSummaryV2 from "../features/kpi-performance/components/WorkSummaryV2";
import WorkSummaryPerPerson from "../features/kpi-performance/components/WorkSummaryPerPerson";
import StagePerformanceCards from "../features/kpi-performance/components/StagePerformanceCards";
import TicketStatusDistribution from "../features/kpi-performance/components/TicketStatusDistribution";
import EffortDistribution from "../features/kpi-performance/components/OpenCustomerSolutionsTickets";
import SummaryKPI from "../features/kpi-performance/components/SummaryKPI";
import EffortSummaryTable from "../features/kpi-performance/components/EffortSummaryTable";
import AvgTimeToClose from "../features/kpi-performance/components/AvgTimetToClose";

import { colors } from "../global";
import apiClient from "../utils/apiClient";

const CHART_KEYS = [
  "work_summary_v2",
  "work_summary_per_person",
  "time_spent_per_category_card",
  "time_spent_per_category_overall_card",
  "ticket_distribution",
  "customer_solution_tickets",
  "overdue_ticket_by_owner",
  "effort_summary",
  "avg_time_to_close",
];

const toCamelCase = (str) =>
  str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

const formatFileSize = (bytes) => {
  if (!bytes) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const getFileIcon = (fileName) => {
  const ext = fileName.split(".").pop().toLowerCase();
  return <FontAwesomeIcon icon={ext === "csv" ? faFileCsv : faFile} />;
};

const sanitizeSheetName = (value) =>
  String(value || "Sheet")
    .replace(/[\\/*?:[\]]/g, " ")
    .slice(0, 31);

const appendSheet = (workbook, rows, sheetName) => {
  if (!Array.isArray(rows) || !rows.length) {
    return;
  }
  const worksheet = XLSX.utils.json_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeSheetName(sheetName));
};

const buildPieRows = (data = {}) =>
  (data.labels || []).map((label, index) => ({
    label,
    value: data.series?.[index] ?? 0,
  }));

const buildLongestTicketRows = (data = {}) =>
  (data.longestTickets || []).map((item) => ({
    name: item.name,
    months: item.months,
  }));

const buildCurrentReportRows = (rows = []) =>
  (rows || []).map((row) => ({
    ...row,
  }));

const KPIPerformance = () => {
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExportingRawDb, setIsExportingRawDb] = useState(false);
  const [dashboardData, setDashboardData] = useState({});

  const chartRefs = useRef(
    CHART_KEYS.reduce((acc, key) => {
      acc[toCamelCase(key)] = React.createRef();
      return acc;
    }, {})
  ).current;

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".csv",
    multiple: false,
  });

  const handleUpload = async () => {
    if (!files.length) {
      toast.error("No files selected");
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));

      const response = await apiClient.post(
        "/api/v1/kPIPerformancev2/upload",
        formData
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.error || "Upload failed");
      }

      setDashboardData(await response.json());
      setFiles([]);
      toast.success("Upload successful");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const captureChart = async (ref) => {
    if (!ref?.current) return null;
    const canvas = await html2canvas(ref.current, {
      useCORS: true,
      scale: 2,
    });
    return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  };

  const handleExportAllChartsToAPI = async () => {
    setIsSubmitting(true);

    try {
      const formData = new FormData();

      for (const key of CHART_KEYS) {
        const blob = await captureChart(chartRefs[toCamelCase(key)]);
        if (blob) {
          formData.append("images", blob, `${key}.png`);
        }
      }

      const response = await apiClient.post(
        "/api/v1/kPIPerformancev2/generate",
        formData
      );

      if (!response.ok) {
        throw new Error("Failed to generate PowerPoint");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Slide_Presentation_${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.pptx`;

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportExcel = () => {
    try {
      if (!Object.keys(dashboardData || {}).length) {
        toast.error("No dashboard data to export");
        return;
      }

      const workbook = XLSX.utils.book_new();

      appendSheet(workbook, dashboardData.workSummary, "Work Summary");
      appendSheet(
        workbook,
        dashboardData.workSummaryPerPerson,
        "Work Summary Per Person"
      );
      appendSheet(
        workbook,
        dashboardData.timeSpentPerCategoryOverall?.rows,
        "Stage Overall"
      );
      appendSheet(
        workbook,
        dashboardData.timeSpentPerCategory?.rows,
        "Stage Detail"
      );
      appendSheet(
        workbook,
        dashboardData.totalTicketPerCategory,
        "Ticket Distribution"
      );
      appendSheet(
        workbook,
        buildPieRows(dashboardData.openCustomerSolutionTicketEffort),
        "Open Ticket Effort"
      );
      appendSheet(
        workbook,
        buildPieRows(dashboardData.overdueTicketsByOwner),
        "Overdue By Owner"
      );
      appendSheet(
        workbook,
        dashboardData.effortSummaryReport?.rows,
        "Effort Summary Rows"
      );

      const effortKpis = dashboardData.effortSummaryReport?.kpis
        ? [dashboardData.effortSummaryReport.kpis]
        : [];
      appendSheet(workbook, effortKpis, "Effort Summary KPI");

      const avgTimeSummary = dashboardData.avgTimetToCloseReport
        ? [
            {
              averageDays: dashboardData.avgTimetToCloseReport.averageDays,
            },
          ]
        : [];
      appendSheet(workbook, avgTimeSummary, "Avg Time To Close");
      appendSheet(
        workbook,
        buildLongestTicketRows(dashboardData.avgTimetToCloseReport),
        "Longest Open Tickets"
      );

      if (!workbook.SheetNames.length) {
        toast.error("No exportable dashboard rows found");
        return;
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      XLSX.writeFile(workbook, `Customer_Solution_KPI_${timestamp}.xlsx`);
      toast.success("Excel export generated");
    } catch (err) {
      toast.error(err.message || "Failed to export Excel");
    }
  };

  const handleExportRawDatabase = async () => {
    setIsExportingRawDb(true);
    try {
      const response = await apiClient.get("/api/v1/kPIPerformancev2/export-db");
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          err.detail || err.error || "Failed to export raw database"
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      link.href = url;
      link.download = match?.[1] || "CSolTicketKPIData_dump.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Raw DB export generated");
    } catch (err) {
      toast.error(err.message || "Failed to export raw database");
    } finally {
      setIsExportingRawDb(false);
    }
  };

  const handleExportCurrentReportData = () => {
    try {
      const currentRows = buildCurrentReportRows(dashboardData.currentReportRows);
      if (!currentRows.length) {
        toast.error("No current report data to export");
        return;
      }

      const workbook = XLSX.utils.book_new();
      appendSheet(workbook, currentRows, "Current Report Data");

      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      XLSX.writeFile(
        workbook,
        `Customer_Solution_KPI_Current_Report_${timestamp}.xlsx`
      );
      toast.success("Current report data export generated");
    } catch (err) {
      toast.error(err.message || "Failed to export current report data");
    }
  };

  return (
    <>
      {isSubmitting && (
        <OverlayLoading
          isVisible={isSubmitting}
          message="Processing your request..."
          icon="truck"
        />
      )}

      <div className="p-6 pb-0">
        <Title title={"KPI Performance"} />
      </div>

      <div className="m-4 p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
        <DropzoneComponent
          getInputProps={getInputProps}
          getRootProps={getRootProps}
          isDragActive={isDragActive}
          fileTypes="CSV"
        />

        {files.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Selected Files ({files.length})
              </h3>
              <div className="text-sm text-gray-500">
                Total:{" "}
                {formatFileSize(
                  files.reduce((acc, file) => acc + file.size, 0)
                )}
              </div>
            </div>
            <ul className="list-disc pl-5">
              {files.map((file, index) => (
                <div
                  key={file.name + index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors mt-3"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {getFileIcon(file.name)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.size)} • CSV File
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </ul>

            <button
              onClick={handleUpload}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faUpload} className="mr-2" /> Upload All
            </button>
          </div>
        )}

        <div className="mt-4">
          <button
            onClick={handleExportRawDatabase}
            className="px-4 py-2 rounded text-white hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: colors.darkGray }}
            disabled={isExportingRawDb || isSubmitting}
          >
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            {isExportingRawDb ? "Exporting Stored DB..." : "Dump Stored DB"}
          </button>
        </div>
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {dashboardData?.workSummary?.length > 0 && (
          <div className="col-span-2">
            <h2 className="text-xl font-semibold mb-3 text-gray-700">
              Work Summary
            </h2>
            <div
              className="bg-white p-4 rounded-lg shadow"
              ref={chartRefs.workSummaryV2}
            >
              <WorkSummaryV2 data={dashboardData.workSummary} />
            </div>
          </div>
        )}

        {dashboardData?.workSummaryPerPerson?.length > 0 && (
          <div className="col-span-2">
            <h2 className="text-xl font-semibold mb-3 text-gray-700">
              Work Summary Per Person
            </h2>
            <div
              className="bg-white p-4 rounded-lg shadow"
              ref={chartRefs.workSummaryPerPerson}
            >
              <WorkSummaryPerPerson data={dashboardData.workSummaryPerPerson} />
            </div>
          </div>
        )}

        {dashboardData?.timeSpentPerCategoryOverall?.rows?.length > 0 && (
          <div>
            <div
              className="bg-white p-4 rounded-lg shadow"
              ref={chartRefs.timeSpentPerCategoryOverallCard}
            >
              <StagePerformanceCards
                data={dashboardData.timeSpentPerCategoryOverall}
              />
            </div>
          </div>
        )}

        {dashboardData?.timeSpentPerCategory?.rows?.length > 0 && (
          <div>
            <div
              className="bg-white p-4 rounded-lg shadow"
              ref={chartRefs.timeSpentPerCategoryCard}
            >
              <StagePerformanceCards
                data={dashboardData.timeSpentPerCategory}
              />
            </div>
          </div>
        )}
        {dashboardData?.totalTicketPerCategory?.length > 0 && (
          <div className="col-span-2">
            <div
              className="bg-white p-4 rounded-lg shadow"
              ref={chartRefs.ticketDistribution}
            >
              <TicketStatusDistribution
                data={dashboardData.totalTicketPerCategory}
              />
            </div>
          </div>
        )}

        {dashboardData?.openCustomerSolutionTicketEffort?.labels?.length >
          0 && (
          <div>
            <h2 className="text-xl font-semibold mb-3 text-gray-700">
              Open Customer Solutions Tickets (Effort)
            </h2>
            <div
              className="bg-white p-4 rounded-lg shadow"
              ref={chartRefs.customerSolutionTickets}
            >
              <EffortDistribution
                data={dashboardData.openCustomerSolutionTicketEffort}
              />
            </div>
          </div>
        )}
        {dashboardData?.overdueTicketsByOwner?.labels?.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-3 text-gray-700">
              Overdue Ticket By Owner
            </h2>
            <div
              className="bg-white p-4 rounded-lg shadow"
              ref={chartRefs.overdueTicketByOwner}
            >
              <EffortDistribution data={dashboardData.overdueTicketsByOwner} />
            </div>
          </div>
        )}
        {dashboardData?.effortSummaryReport?.kpis?.totalTickets > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-3 text-gray-700">
              Effort Summary
            </h2>
            <div
              className="bg-white p-4 rounded-lg shadow"
              ref={chartRefs.effortSummary}
            >
              <SummaryKPI kpis={dashboardData?.effortSummaryReport?.kpis} />
              <EffortSummaryTable
                rows={dashboardData?.effortSummaryReport?.rows}
              />
            </div>
          </div>
        )}
        {dashboardData?.avgTimetToCloseReport?.averageDays && (
          <div>
            <h2 className="text-xl font-semibold mb-3 text-gray-700">
              Average Time To Close
            </h2>
            <div
              className="bg-white p-4 rounded-lg shadow"
              ref={chartRefs.avgTimeToClose}
            >
              <AvgTimeToClose data={dashboardData?.avgTimetToCloseReport} />
            </div>
          </div>
        )}
      </div>

      {Object.keys(dashboardData).length > 0 && (
        <div className="flex flex-wrap gap-3 px-4 pb-4">
          <button
            onClick={handleExportCurrentReportData}
            className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            style={{
              backgroundColor: colors.darkGray,
              color: colors.white,
            }}
          >
            <FontAwesomeIcon icon={faDownload} />
            Export Current Report Data
          </button>
          <button
            onClick={handleExportExcel}
            className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            style={{
              backgroundColor: colors.primary,
              color: colors.white,
            }}
          >
            <FontAwesomeIcon icon={faDownload} />
            Export Excel
          </button>
          <button
            onClick={handleExportAllChartsToAPI}
            className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
            style={{
              backgroundColor: colors.success,
              color: colors.white,
            }}
          >
            Export PowerPoint
          </button>
        </div>
      )}
    </>
  );
};

export default KPIPerformance;
